import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { useLanguage } from '@/context/LanguageContext'
import { CreateInvoiceReq, ProductType } from '@/services/mobi-api/invoice/types'
import useIntl from '@/utils/intl'
import { TextField, IconButton } from '@mui/material'
import { Edit } from '@mui/icons-material'
import React, { FunctionComponent, useEffect, useReducer } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { useMobiApi } from '@/services/mobi-api'
import { useToast } from '@/context/ToastContext'
import Loader from '@/components/ui/loader'
import MCButton from '@/components/ui/Buttons/MobicomButton'
import Image from 'next/image'
import { resolveImageUrl } from '@/utils/constants'
import { getSystemEnv } from '@/utils/env'
import { useAuth } from '@/context/AuthContext'
import { InstanceNewNumberV2 } from '@/services/mobi-api/numberv2/types'
import { isMobile } from '@/utils/helpers'
import { useTheme } from '@/context/ThemeContext'
import {
  normalizeTrackingText,
  trackNewNumberEvent
} from '@/utils/newNumberFlowTracking'

interface Props {
  instanceId?: string
  initialEmail?: string
}

interface State {
  corpId?: string
  checked?: boolean
  vatType?: string
  hasPrice?: boolean
  loading?: boolean
  openTerms?: boolean
  instance?: InstanceNewNumberV2
  id?: string
  email?: string
}
const Step4: FunctionComponent<Props> = ({ instanceId, initialEmail }) => {
  const router = useRouter()
  const { isWebView } = useAuth()
  const { locale } = useLanguage()
  const { numberv2, invoice } = useMobiApi()
  const { showError } = useToast()
  const searchParams = useSearchParams()
  const { theme } = useTheme()

  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {
      corpId: '',
      checked: false,
      vatType: 'CONSUMER',
      loading: true,
      email: initialEmail ?? ''
    })
  const [isEmailEditing, setIsEmailEditing] = React.useState(false)
  const { translate } = useIntl()
  const startedAtRef = React.useRef(Date.now())

  const fetchRequest = React.useCallback(async (_id: string): Promise<void> => {
    try {
      const res = await numberv2.getInstanceV2(_id, locale)
      if (res !== undefined && res.error === undefined) {
        setState({ instance: res, email: res.email ?? initialEmail ?? '' })
        if ((res.configuratorRes?.price?.totalAmount ?? 0) > 0) {
          setState({ hasPrice: true })
        }
      } else {
        trackNewNumberEvent('step_error', {
          step_name: 'payment',
          error_type: normalizeTrackingText(res?.error ?? 'payment_info_fetch_failed')
        })
        showError(locale === 'en' ? (res?.cause?.messageEn ?? (res?.error ?? translate('request.failed'))) : res?.cause?.messageMn ?? (res?.error ?? translate('request.failed')))
      }
    } catch (e) {
      trackNewNumberEvent('step_error', {
        step_name: 'payment',
        error_type: 'payment_info_fetch_failed'
      })
      console.error(e)
      showError(translate('error'))
    } finally {
      setState({ loading: false })
    }
  }, [locale, numberv2, showError, translate])

  const handleQuery = React.useCallback((): void => {
    const resolvedId = instanceId ?? searchParams.get('_id') ?? ''
    setState({ id: resolvedId })
    if (resolvedId !== '') {
      void fetchRequest(resolvedId)
    } else {
      setState({ loading: false })
    }
    // else {
    //   showError(translate('error'))
    //   router.push('/')
    // }
  }, [fetchRequest, instanceId, searchParams])

  useEffect(() => {
    handleQuery()
  }, [])

  useEffect(() => {
    trackNewNumberEvent('page_view_step', {
      step_name: 'payment'
    })
    trackNewNumberEvent('view_payment', {
      step_name: 'payment'
    })
    startedAtRef.current = Date.now()

    return () => {
      trackNewNumberEvent('time_spent_step', {
        step_name: 'payment',
        time_spent_seconds: Math.max(
          1,
          Math.round((Date.now() - startedAtRef.current) / 1000)
        )
      })
    }
  }, [])

  const VAT = [
    {
      name: translate('shop.vatCons'),
      icon: '/uploads/icons/user1.svg',
      darkIcon: '/uploads/icons/user.svg',
      type: 'CONSUMER'
    },
    {
      name: translate('shop.vatCorp'),
      icon: '/uploads/icons/briefcase.svg',
      darkIcon: '/uploads/icons/briefcase1.svg',
      type: 'CORPORATE'
    }
  ]

  const handleChangeVatType = (value: string): void => {
    setState({ vatType: value })
  }
  const handleContinue = async (): Promise<void> => {
    trackNewNumberEvent('click_cta', {
      step_name: 'payment',
      button_name: 'continue'
    })
    trackNewNumberEvent('payment_attempt', {
      step_name: 'payment',
      amount: state.instance?.configuratorRes?.price?.totalAmount ?? 0
    })
    const payload: CreateInvoiceReq = {
      isdn: state.instance?.newNumber,
      products: [{ productType: ProductType.NEWNUMBER, price: state.instance?.configuratorRes?.price?.totalAmount ?? 0, quantity: 1, productCode: state.id ?? '' }],
      customerType: state.vatType === 'CORPORATE' ? 'CORPORATE' : 'CONSUMER',
      languageCode: locale,
      vatEmail: state.email ?? state.instance?.email,
      corpId: state.vatType === 'CORPORATE' ? state.corpId : undefined,
      device: isMobile(),
      redirectUrl: getSystemEnv().DOMAIN + '/paymentSuccess?id=' + (state.instance?._id ?? '')
    }
    try {
      const res = await invoice.create(payload)
      if (res?.invoiceId === null || res?.invoiceId === undefined || res?.invoiceId === '') {
        trackNewNumberEvent('payment_fail', {
          step_name: 'payment',
          error_reason: 'invoice_id_missing'
        })
        showError(translate('error'))
      } else {
        if (isWebView) {
          const data = {
            key: 'invoice',
            value: {
              command: 'newnumber',
              identifier: state.instance?.newNumber,
              product: state.instance?._id,
              email: state.email ?? state.instance?.email,
              amount: state.instance?.configuratorRes?.price?.totalAmount ?? 0,
              register: state.vatType === 'CORPORATE' ? state.corpId : '',
              vatType: state.vatType === 'CORPORATE' ? 'CORP' : 'CONS',
              activationCode: res?.invoiceId,
              productName: translate('quick.newNumber')
            }
          }
          let webView
          if ((window as any).mobiBridge !== undefined) {
            webView = (window as any).mobiBridge
          }
          if ((window as any).ReactNativeWebview !== undefined) {
            webView = (window as any).ReactNativeWebview
          }
          if (webView !== undefined) {
            webView.postMessage(JSON.stringify(data))
          }
        } else if (res?.redirectUrl !== undefined && res.redirectUrl !== '') {
          router.push(res.redirectUrl)
        } else {
          trackNewNumberEvent('payment_fail', {
            step_name: 'payment',
            error_reason: 'redirect_url_missing'
          })
          showError(translate('error'))
        }
      }
    } catch (e) {
      trackNewNumberEvent('payment_fail', {
        step_name: 'payment',
        error_reason: 'invoice_create_failed'
      })
      showError(translate('error'))
      console.error(e)
    }
  }
  const disableContinue = (): boolean => {
    let check = false
    if (state.vatType === 'CORPORATE' && (state.corpId?.length ?? 0) < 7) { check = true }
    if (state.vatType === '') { check = true }
    return check
  }

  if (state.loading === true) {
    return <div className='w-full h-96 flex items-center justify-center'><Loader /></div>
  }
  if (state.hasPrice === true) {
    return (
      <div className='bg-surfaceInverse rounded-xl md:shadow w-full h-full md:h-[75vh] md:w-fit flex flex-col justify-between overflow-auto'>
        <div className='flex-grow overflow-auto'>
          <div className='md:p-8 p-4 flex flex-col gap-4 border-b border-secondaryMidStroke'>
            <span className='text-textMid text-xs md:text-sm font-bold'>{translate('order.summary')}</span>
            <div className='py-2 bg-surfaceWebGrey border border-secondaryMidStroke w-full rounded-xl'>
              <div className='flex justify-between px-4 py-2'>
                <span className='text-textMid text-xs md:text-sm font-medium'>{translate('phoneNumber')}:</span>
                <span className='text-textHigh text-xs md:text-sm font-bold'>{state.instance?.newNumber}</span>
              </div>
              <div className='flex justify-between px-4 py-2'>
                <span className='text-textMid text-xs md:text-sm font-medium'>{translate('product')}:</span>
                <span className='text-textHigh text-xs md:text-sm font-bold'>{state.instance?.choosenPackage?.category.categoryName} - {state.instance?.choosenPackage?.subCategory.name}</span>
              </div>
              <div className='flex justify-between px-4 py-2'>
                <span className='text-textMid text-xs md:text-sm font-medium'>{translate('price')}:</span>
                <span className='text-textHigh text-xs md:text-sm font-bold'>{state.instance?.configuratorRes?.price?.totalAmount ?? 0}₮</span>
              </div>
            </div>
          </div>
          <div className='md:p-8 p-4'>
            <span className='text-textMid text-xs md:text-sm font-bold'>{translate('shop.vat')}</span>
            <div className='flex gap-4 mt-4 w-full  md:w-96'>
              {VAT.map((vat, index) => (
                <MCButton selected={vat.type === state.vatType} type='shop' fullwidth size='sm' onClick={() => handleChangeVatType(vat.type)} key={index} leadingIconImg={theme === 'dark' ? resolveImageUrl(vat.darkIcon) : resolveImageUrl(vat.icon)}>{vat.name}</MCButton>
              ))}
            </div>
            <div className='md:w-96 w-full'>
              <TextField
                label={state.vatType === 'CORPORATE' ? translate('shop.corpEmail') : translate('shop.vatEmail')}
                variant='outlined'
                className='w-full rounded-xl border-secondaryHighStroke bg-surfaceInverse text-textHigh mt-6'
                value={state.email ?? ''}
                onChange={(e) => { if (isEmailEditing) setState({ email: e.target.value }) }}
                InputLabelProps={{
                  style: { color: 'var(--color-text-textHigh)' }
                }}
                InputProps={{
                  readOnly: !isEmailEditing,
                  endAdornment: (
                    <IconButton size='small' onClick={() => setIsEmailEditing(true)}>
                      <Edit fontSize='small' sx={{ color: 'var(--color-text-textHigh)' }} />
                    </IconButton>
                  ),
                  style: {
                    color: 'var(--color-text-textHigh)',
                    '&::placeholder': {
                      color: 'var(--color-text-secondaryHighStroke)'
                    }
                  } as any
                }}
                sx={{
                  '& fieldset': {
                    borderRadius: '8px'
                  }
                }}
              />
              {
                state.vatType === 'CORPORATE'
                  ? (
                    <TextField
                      type='string'
                      value={state.corpId}
                      label={translate('shop.vatCorpId')}
                      variant='outlined'
                      className='w-full rounded-xl border-secondaryHighStroke bg-surfaceInverse text-textHigh mt-6'
                      onChange={(e) => {
                        setState({ corpId: e.target.value })
                      }}
                      InputLabelProps={{
                        style: { color: 'var(--color-text-textHigh)' }
                      }}
                      InputProps={{
                        style: {
                          color: 'var(--color-text-textHigh)',
                          '&::placeholder': {
                            color: 'var(--color-text-secondaryHighStroke)'
                          }
                        } as any
                      }}
                      sx={{
                        '& fieldset': {
                          borderRadius: '8px'
                        }
                      }}
                    />
                    )
                  : <></>
            }
            </div>
          </div>
        </div>
        <div className='fixed md:sticky bottom-0 right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
          {
            isWebView
              ? <MCButton fullwidth disabled={disableContinue()} onClick={() => { void handleContinue() }}>{translate('continue')}</MCButton>
              : <DarkButton fullwidth disabled={disableContinue()} onClick={() => { void handleContinue() }}>{translate('continue')}</DarkButton>
          }
        </div>
      </div>
    )
  } else {
    return (
      <div className='border border-[#4ADE80] rounded-lg lg:p-10 p-6 xl:w-fit w-full m-4 md:w-1/2'>
        <Image draggable={false} width={60} height={60} alt='img' src={resolveImageUrl('/uploads/icons/payment-success.png')} className='xl:w-16 w-12' />
        <h1 className='text-textHigh xl:text-2xl text-lg font-bold mt-4'>{translate('success')}!</h1>
        <p className='text-textMid xl:text-sm text-xs font-medium mt-4'>{translate('thankyoutext')}</p>
        {
            state.instance?.simType === 'ESIM' && (
              <p className='text-textMid text-xs xl:text-sm font-bold 2xl:w-[500px] xl:w-[400px] mt-8 text-center'>{translate('sim.qrsent').replace('$email', state.instance?.email ?? '')}</p>
            )
        }
      </div>
    )
  }
}
export default Step4
